import React, { useState, useEffect, useCallback } from 'react';
import { AuditNotification } from '../../types';
import { auditService } from '../../services/auditService';
import { Eye, X, CheckCheck } from 'lucide-react';

/**
 * 감사 조회 통지 배너 — super_admin 전용.
 * 감사자가 메시지를 열람하면 그 사실(감사자·대상·사유)이 최고 관리자에게 온다.
 * 피감사자에게는 통지하지 않는다(2026-09 결정 — App.tsx가 role === 'super_admin'일 때만 마운트하고
 * 서버 라우트도 requireSuperAdmin이라 다른 역할은 403을 받아 조용히 비어 있다).
 */
interface AuditNotificationBannerProps {
  userId: string;
}

export const AuditNotificationBanner: React.FC<AuditNotificationBannerProps> = ({ userId }) => {
  const [notifications, setNotifications] = useState<AuditNotification[]>([]);
  const [expanded, setExpanded] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await auditService.getNotifications();
      setNotifications(data);
    } catch (err) {
      // 권한이 없는 경우 등 무시
    }
  }, []);

  // 콜드 스타트 1회 조회. 이후는 소켓이 담당한다 —
  // 알림 발생(audit:notification)과 읽음 처리(audit:notification-read)가 모두 전파되므로
  // 30초 폴링은 더 이상 필요 없다.
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Socket 이벤트를 통한 실시간 알림 수신 (socketService가 CustomEvent로 전달)
  useEffect(() => {
    const onNew = () => loadNotifications();
    // 다른 기기에서 읽음 처리한 경우 — 이 기기 배너에서도 즉시 지운다.
    const onRead = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.all) {
        setNotifications([]);
      } else if (detail.notificationId != null) {
        setNotifications(prev => prev.filter(n => n.id !== detail.notificationId));
      }
    };
    window.addEventListener('audit:notification' as any, onNew);
    window.addEventListener('audit:notification-read' as any, onRead);
    return () => {
      window.removeEventListener('audit:notification' as any, onNew);
      window.removeEventListener('audit:notification-read' as any, onRead);
    };
  }, [loadNotifications]);

  const handleDismiss = async (id: number) => {
    try {
      await auditService.markNotificationRead(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  const handleDismissAll = async () => {
    try {
      await auditService.markAllNotificationsRead();
      setNotifications([]);
    } catch (err) {
      console.error('Failed to dismiss all notifications:', err);
    }
  };

  if (notifications.length === 0) return null;

  return (
    // top: PC앱 제목 표시줄(창 버튼) 아래로 — TitleBar의 --titlebar-h 주석
    <div className="fixed top-[calc(var(--titlebar-h,0px)+1rem)] right-4 z-50 max-w-sm">
      {/* Badge */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg shadow-lg hover:bg-amber-600 transition-colors text-sm"
      >
        <Eye size={16} />
        <span>감사 조회 알림 {notifications.length}건</span>
      </button>

      {/* Notification list */}
      {expanded && (
        <div className="mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden max-h-[400px]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <span className="text-sm font-medium text-slate-700">감사 조회 알림</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDismissAll}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-brand-600 transition-colors"
              >
                <CheckCheck size={12} />
                모두 읽음
              </button>
              <button
                onClick={() => setExpanded(false)}
                aria-label="닫기"
                className="p-1.5 rounded text-slate-400 hover:text-slate-600"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-[320px]">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notif.createdAt).toLocaleString('ko-KR')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDismiss(notif.id)}
                    className="p-2 -m-1 rounded text-slate-400 hover:text-slate-600 shrink-0"
                    title="읽음 처리"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
